import { action, computed, makeObservable, observable, runInAction } from "mobx"

import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { LiquidityPoolByTags, LiquidityPoolType } from "@/domains/core"
import { SortDirection, SortType } from "@/domains/table-processor"

// Type for sortBy object structure
export interface SortBy {
  key: string
  direction: SortDirection
  type: SortType
}

// Search param sources following the multi-source pattern
export interface SearchParamSources {
  urlParam: string | undefined
  localstorage: string | undefined
  default: string
  ui: string | undefined
}

// SortBy param sources
export interface SortByParamSources {
  urlParam: SortBy | undefined
  localstorage: SortBy | undefined
  default: SortBy
  ui: SortBy | undefined
}

// PageSize param sources
export interface PageSizeParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

// StakedOnly param sources
export interface StakedOnlyParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// ShowInactivePairs param sources
export interface ShowInactivePairsParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// PoolByTags param sources
export interface PoolByTagsParamSources {
  urlParam: LiquidityPoolByTags | undefined
  localstorage: LiquidityPoolByTags | undefined
  default: LiquidityPoolByTags
  ui: LiquidityPoolByTags | undefined
}

// PoolType param sources
export interface PoolTypeParamSources {
  urlParam: LiquidityPoolType | undefined
  localstorage: LiquidityPoolType | undefined
  default: LiquidityPoolType
  ui: LiquidityPoolType | undefined
}

// Detailed param sources
export interface IsDetailedParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// CurrentPageNumber param sources
export interface CurrentPageNumberParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

export class LiquidityPageStore {
  searchParamSources: SearchParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "",
    ui: undefined,
  }

  sortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "tvlUsd", direction: "desc", type: "number" },
    ui: undefined,
  }

  pageSizeParamSources: PageSizeParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 10,
    ui: undefined,
  }

  stakedOnlyParamSources: StakedOnlyParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  showInactivePairsParamSources: ShowInactivePairsParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  poolByTagsParamSources: PoolByTagsParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "active",
    ui: undefined,
  }

  poolTypeParamSources: PoolTypeParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "all",
    ui: undefined,
  }

  isDetailedParamSources: IsDetailedParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  currentPageNumberParamSources: CurrentPageNumberParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 1,
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
  ) {
    makeObservable(this, {
      searchParamSources: observable,
      sortByParamSources: observable,
      pageSizeParamSources: observable,
      stakedOnlyParamSources: observable,
      showInactivePairsParamSources: observable,
      poolByTagsParamSources: observable,
      poolTypeParamSources: observable,
      isDetailedParamSources: observable,
      currentPageNumberParamSources: observable,
      search: computed,
      sortBy: computed,
      pageSize: computed,
      stakedOnly: computed,
      poolByTags: computed,
      poolType: computed,
      isDetailed: computed,
      currentPageNumber: computed,
      setSearchFromUrlParam: action,
      setSearchFromLocalStorage: action,
      setSearchFromUi: action,
      setSortByFromUrlParam: action,
      setSortByFromLocalStorage: action,
      setSortByFromUi: action,
      setPageSizeFromUrlParam: action,
      setPageSizeFromLocalStorage: action,
      setPageSizeFromUi: action,
      setStakedOnlyFromUrlParam: action,
      setStakedOnlyFromLocalStorage: action,
      setStakedOnlyFromUi: action,
      setPoolByTagsFromUrlParam: action,
      setPoolByTagsFromLocalStorage: action,
      setPoolByTagsFromUi: action,
      setPoolTypeFromUrlParam: action,
      setPoolTypeFromLocalStorage: action,
      setPoolTypeFromUi: action,
      setIsDetailedFromUrlParam: action,
      setIsDetailedFromLocalStorage: action,
      setIsDetailedFromUi: action,
      setCurrentPageNumberFromUrlParam: action,
      setCurrentPageNumberFromLocalStorage: action,
      setCurrentPageNumberFromUi: action,
      clearFiltersForNewPool: action,
    })
  }

  /**
   * Reset search and filters to defaults so the user can find a newly created pool on the liquidity list.
   */
  clearFiltersForNewPool(): void {
    this.setSearchFromUi("")
    this.setSortByFromUi(this.sortByParamSources.default)
    this.setPageSizeFromUi(this.pageSizeParamSources.default)
    this.setStakedOnlyFromUi(this.stakedOnlyParamSources.default)
    this.setShowInactivePairsFromUi(this.showInactivePairsParamSources.default)
    this.setPoolByTagsFromUi(this.poolByTagsParamSources.default)
    this.setPoolTypeFromUi(this.poolTypeParamSources.default)
    this.setCurrentPageNumberFromUi(this.currentPageNumberParamSources.default)
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  private initializeFromStorage(): void {
    // Initialize search from localStorage
    const localSearch = this.storage.getItem(`${this.chainSlug}-liquidity-search`)
    if (localSearch) {
      this.setSearchFromLocalStorage(localSearch)
    }

    // Initialize sortBy from localStorage
    const localSortBy = this.storage.getItem(`${this.chainSlug}-liquidity-sortBy`)
    if (localSortBy) {
      try {
        const parsedSortBy = JSON.parse(localSortBy) as SortBy
        this.setSortByFromLocalStorage(parsedSortBy)
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Initialize pageSize from localStorage
    const localPageSize = this.storage.getItem(`${this.chainSlug}-liquidity-pageSize`)
    if (localPageSize) {
      const parsedPageSize = Number(localPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromLocalStorage(parsedPageSize)
      }
    }

    // Initialize stakedOnly from localStorage
    const localStakedOnly = this.storage.getItem(`${this.chainSlug}-liquidity-stakedOnly`)
    if (localStakedOnly) {
      const parsedStakedOnly = localStakedOnly === "true"
      this.setStakedOnlyFromLocalStorage(parsedStakedOnly)
    }

    // Initialize showInactivePairs from localStorage
    const localShowInactivePairs = this.storage.getItem(`${this.chainSlug}-liquidity-showInactivePairs`)
    if (localShowInactivePairs) {
      const parsedInactiveJunkPairs = localShowInactivePairs === "true"
      this.setShowInactivePairsFromLocalStorage(parsedInactiveJunkPairs)
    }

    // Initialize poolByTags from localStorage
    const localPoolByTags = this.storage.getItem(`${this.chainSlug}-liquidity-poolByTags`)
    if (localPoolByTags && ["active", "new", "others"].includes(localPoolByTags)) {
      this.setPoolByTagsFromLocalStorage(localPoolByTags as LiquidityPoolByTags)
    }

    // Initialize poolType from localStorage
    const localPoolType = this.storage.getItem(`${this.chainSlug}-liquidity-poolType`)
    if (localPoolType && ["all", "stable", "volatile", "cl"].includes(localPoolType)) {
      this.setPoolTypeFromLocalStorage(localPoolType as LiquidityPoolType)
    }

    // Initialize detailed from localStorage
    const localDetailed = this.storage.getItem(`${this.chainSlug}-liquidity-is-detailed`)
    if (localDetailed) {
      const parsedDetailed = localDetailed === "true"
      this.setIsDetailedFromLocalStorage(parsedDetailed)
    }

    // Initialize currentPageNumber from localStorage
    const localCurrentPageNumber = this.storage.getItem(`${this.chainSlug}-liquidity-currentPageNumber`)
    if (localCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(localCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromLocalStorage(parsedCurrentPageNumber)
      }
    }
  }

  public initializeFromUrlParams(): void {
    // Initialize search from URL params
    const urlSearch = this.urlParams.getParam("search")
    if (urlSearch) {
      this.setSearchFromUrlParam(urlSearch)
    }

    // Initialize sortBy from URL params (combining sortKey, sortDir, sortType)
    const urlSortKey = this.urlParams.getParam("sortKey")
    const urlSortDir = this.urlParams.getParam("sortDir")
    const urlSortType = this.urlParams.getParam("sortType")

    if (urlSortKey && urlSortDir && urlSortType) {
      const sortBy: SortBy = {
        key: urlSortKey,
        direction: urlSortDir as SortDirection,
        type: urlSortType as SortType,
      }
      this.setSortByFromUrlParam(sortBy)
    }

    // Initialize pageSize from URL params
    const urlPageSize = this.urlParams.getParam("pageSize")
    if (urlPageSize) {
      const parsedPageSize = Number(urlPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromUrlParam(parsedPageSize)
      }
    }

    // Initialize stakedOnly from URL params (URL key is "staked")
    const urlStakedOnly = this.urlParams.getParam("staked")
    if (urlStakedOnly) {
      const parsedStakedOnly = urlStakedOnly === "true"
      this.setStakedOnlyFromUrlParam(parsedStakedOnly)
    }

    // Initialize showInactivePairs from URL params (URL key is "showInactivePairs")
    const urlShowInactivePairs = this.urlParams.getParam("showInactivePairs")
    if (urlShowInactivePairs) {
      const parsedShowInactivePairs = urlShowInactivePairs === "true"
      this.setShowInactivePairsFromUrlParam(parsedShowInactivePairs)
    }

    // Initialize poolByTags from URL params (URL key is "tags")
    const urlPoolByTags = this.urlParams.getParam("tags")
    if (urlPoolByTags && ["active", "new", "others"].includes(urlPoolByTags)) {
      this.setPoolByTagsFromUrlParam(urlPoolByTags as LiquidityPoolByTags)
    }

    // Initialize poolType from URL params (URL key is "type")
    const urlPoolType = this.urlParams.getParam("type")
    if (urlPoolType && ["all", "stable", "volatile", "cl"].includes(urlPoolType)) {
      this.setPoolTypeFromUrlParam(urlPoolType as LiquidityPoolType)
    }

    // Initialize detailed from URL params (URL key is "detailed")
    const urlDetailed = this.urlParams.getParam("detailed")
    if (urlDetailed) {
      const parsedDetailed = urlDetailed === "true"
      this.setIsDetailedFromUrlParam(parsedDetailed)
    }

    // Initialize currentPageNumber from URL params (URL key is "currentPage")
    const urlCurrentPageNumber = this.urlParams.getParam("currentPage")
    if (urlCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(urlCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromUrlParam(parsedCurrentPageNumber)
      }
    }
  }

  get search(): string {
    return (
      this.searchParamSources.ui ??
      this.searchParamSources.urlParam ??
      this.searchParamSources.localstorage ??
      this.searchParamSources.default
    )
  }

  get sortBy(): SortBy {
    return (
      this.sortByParamSources.ui ??
      this.sortByParamSources.urlParam ??
      this.sortByParamSources.localstorage ??
      this.sortByParamSources.default
    )
  }

  get pageSize(): number {
    return (
      this.pageSizeParamSources.ui ??
      this.pageSizeParamSources.urlParam ??
      this.pageSizeParamSources.localstorage ??
      this.pageSizeParamSources.default
    )
  }

  get stakedOnly(): boolean {
    return (
      this.stakedOnlyParamSources.ui ??
      this.stakedOnlyParamSources.urlParam ??
      this.stakedOnlyParamSources.localstorage ??
      this.stakedOnlyParamSources.default
    )
  }

  get showInactivePairs(): boolean {
    return (
      this.showInactivePairsParamSources.ui ??
      this.showInactivePairsParamSources.urlParam ??
      this.showInactivePairsParamSources.localstorage ??
      this.showInactivePairsParamSources.default
    )
  }

  get poolByTags(): LiquidityPoolByTags {
    return (
      this.poolByTagsParamSources.ui ??
      this.poolByTagsParamSources.urlParam ??
      this.poolByTagsParamSources.localstorage ??
      this.poolByTagsParamSources.default
    )
  }

  get poolType(): LiquidityPoolType {
    return (
      this.poolTypeParamSources.ui ??
      this.poolTypeParamSources.urlParam ??
      this.poolTypeParamSources.localstorage ??
      this.poolTypeParamSources.default
    )
  }

  get isDetailed(): boolean {
    return (
      this.isDetailedParamSources.ui ??
      this.isDetailedParamSources.urlParam ??
      this.isDetailedParamSources.localstorage ??
      this.isDetailedParamSources.default
    )
  }

  get currentPageNumber(): number {
    return (
      this.currentPageNumberParamSources.ui ??
      this.currentPageNumberParamSources.urlParam ??
      this.currentPageNumberParamSources.localstorage ??
      this.currentPageNumberParamSources.default
    )
  }

  setSearchFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.urlParam === value) return
    this.searchParamSources.urlParam = value
  }

  setSearchFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.localstorage === value) return
    this.searchParamSources.localstorage = value
  }

  setSearchFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.ui === value) return
    this.searchParamSources.ui = value

    // Auto-sync to storage and URL params.
    this.storage.setItem(`${this.chainSlug}-liquidity-search`, value)
    this.urlParams.setParam("search", value)
  }

  // SortBy setters
  setSortByFromUrlParam(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.urlParam?.key === value.key &&
      this.sortByParamSources.urlParam?.direction === value.direction &&
      this.sortByParamSources.urlParam?.type === value.type
    )
      return
    this.sortByParamSources.urlParam = value
  }

  setSortByFromLocalStorage(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.localstorage?.key === value.key &&
      this.sortByParamSources.localstorage?.direction === value.direction &&
      this.sortByParamSources.localstorage?.type === value.type
    )
      return
    this.sortByParamSources.localstorage = value
  }

  setSortByFromUi(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.ui?.key === value.key &&
      this.sortByParamSources.ui?.direction === value.direction &&
      this.sortByParamSources.ui?.type === value.type
    )
      return
    this.sortByParamSources.ui = value

    // Auto-sync to storage (spread sortBy into individual URL params)
    this.storage.setItem(`${this.chainSlug}-liquidity-sortBy`, JSON.stringify(value))
    this.urlParams.setParam("sortKey", value.key)
    this.urlParams.setParam("sortDir", value.direction)
    this.urlParams.setParam("sortType", value.type)
  }

  // PageSize setters
  setPageSizeFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.urlParam === value) return
    this.pageSizeParamSources.urlParam = value
  }

  setPageSizeFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.localstorage === value) return
    this.pageSizeParamSources.localstorage = value
  }

  setPageSizeFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.ui === value) return
    this.pageSizeParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-liquidity-pageSize`, value.toString())
    this.urlParams.setParam("pageSize", value.toString())
  }

  // StakedOnly setters
  setStakedOnlyFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.urlParam === value) return
    this.stakedOnlyParamSources.urlParam = value
  }

  setStakedOnlyFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.localstorage === value) return
    this.stakedOnlyParamSources.localstorage = value
  }

  setStakedOnlyFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.ui === value) return
    this.stakedOnlyParamSources.ui = value

    // Auto-sync to storage (URL key is "staked")
    this.storage.setItem(`${this.chainSlug}-liquidity-stakedOnly`, value.toString())
    this.urlParams.setParam("staked", value.toString())
  }

  // ShowInactivePairs setters
  setShowInactivePairsFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.showInactivePairsParamSources.urlParam === value) return
    this.showInactivePairsParamSources.urlParam = value
  }

  setShowInactivePairsFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.showInactivePairsParamSources.localstorage === value) return
    this.showInactivePairsParamSources.localstorage = value
  }

  setShowInactivePairsFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.showInactivePairsParamSources.ui === value) return
    this.showInactivePairsParamSources.ui = value

    // Auto-sync to storage (URL key is "showInactivePairs")
    this.storage.setItem(`${this.chainSlug}-liquidity-showInactivePairs`, value.toString())
    this.urlParams.setParam("showInactivePairs", value.toString())
  }

  // PoolByTags setters
  setPoolByTagsFromUrlParam(value: LiquidityPoolByTags | undefined): void {
    if (!value) return
    if (this.poolByTagsParamSources.urlParam === value) return
    this.poolByTagsParamSources.urlParam = value
  }

  setPoolByTagsFromLocalStorage(value: LiquidityPoolByTags | undefined): void {
    if (!value) return
    if (this.poolByTagsParamSources.localstorage === value) return
    this.poolByTagsParamSources.localstorage = value
  }

  setPoolByTagsFromUi(value: LiquidityPoolByTags | undefined): void {
    if (!value) return
    if (this.poolByTagsParamSources.ui === value) return
    this.poolByTagsParamSources.ui = value

    // Auto-sync to storage (URL key is "tags")
    this.storage.setItem(`${this.chainSlug}-liquidity-poolByTags`, value)
    this.urlParams.setParam("tags", value)
  }

  // PoolType setters
  setPoolTypeFromUrlParam(value: LiquidityPoolType | undefined): void {
    if (!value) return
    if (this.poolTypeParamSources.urlParam === value) return
    this.poolTypeParamSources.urlParam = value
  }

  setPoolTypeFromLocalStorage(value: LiquidityPoolType | undefined): void {
    if (!value) return
    if (this.poolTypeParamSources.localstorage === value) return
    this.poolTypeParamSources.localstorage = value
  }

  setPoolTypeFromUi(value: LiquidityPoolType | undefined): void {
    if (!value) return
    if (this.poolTypeParamSources.ui === value) return
    this.poolTypeParamSources.ui = value

    // Auto-sync to storage (URL key is "type")
    this.storage.setItem(`${this.chainSlug}-liquidity-poolType`, value)
    this.urlParams.setParam("type", value)
  }

  // Detailed setters
  setIsDetailedFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.urlParam === value) return
    this.isDetailedParamSources.urlParam = value
  }

  setIsDetailedFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.localstorage === value) return
    this.isDetailedParamSources.localstorage = value
  }

  setIsDetailedFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.ui === value) return
    this.isDetailedParamSources.ui = value

    // Auto-sync to storage (URL key is "detailed")
    this.storage.setItem(`${this.chainSlug}-liquidity-is-detailed`, value.toString())
    this.urlParams.setParam("is-detailed", value.toString())
  }

  // CurrentPageNumber setters
  setCurrentPageNumberFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.urlParam === value) return
    this.currentPageNumberParamSources.urlParam = value
  }

  setCurrentPageNumberFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.localstorage === value) return
    this.currentPageNumberParamSources.localstorage = value
  }

  setCurrentPageNumberFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.ui === value) return
    this.currentPageNumberParamSources.ui = value

    // Auto-sync to storage (URL key is "currentPage")
    this.storage.setItem(`${this.chainSlug}-liquidity-currentPageNumber`, value.toString())
    this.urlParams.setParam("currentPage", value.toString())
  }
}
